import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { WarningCircle } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { RadioGroupItem } from "@/components/ui/radio-group"
import { useSettings } from "@/context/SettingsContext"
import { directionOf } from "@/lib/languages"

/**
 * A labelled group of radios: a <fieldset> (role radiogroup) named by its
 * <legend>, an optional hint and error, and one <Radio> per choice. Arrow
 * keys move and choose, in the reading direction (Arabic flips them); Tab
 * enters on the chosen one.
 *
 *   <RadioGroup legend="Trip" value={trip} onValueChange={setTrip} orientation="horizontal">
 *     <Radio value="round">Round trip</Radio>
 *     <Radio value="oneway" hint="No return flight">One way</Radio>
 *   </RadioGroup>
 *
 * Every row is a 44px target: the whole row is the label.
 */
const join = (...ids) => ids.filter(Boolean).join(" ") || undefined

export function useReadingDirection(dir) {
  const settings = useSettings()
  return dir ?? (settings?.lang ? directionOf(settings.lang) : undefined)
}

export const RadioGroup = React.forwardRef(({
  legend, hint, error, required = false, orientation = "vertical", dir,
  className, legendClassName, children, ...props
}, ref) => {
  const direction = useReadingDirection(dir)
  const auto = React.useId()
  const legendId = `${auto}legend`
  const hintId = hint ? `${auto}hint` : undefined
  const errorId = error ? `${auto}error` : undefined
  return (
    <RadioGroupPrimitive.Root
      asChild
      ref={ref}
      dir={direction}
      orientation={orientation}
      required={required}
      aria-labelledby={legendId}
      aria-describedby={join(hintId, errorId)}
      aria-invalid={error ? true : undefined}
      {...props}>
      <fieldset
        className={cn("group/radios m-0 min-w-0 border-0 p-0", className)}
        data-invalid={error ? "" : undefined}>
        <legend
          id={legendId}
          className={cn("mb-1 p-0 text-label-md font-semibold text-touki-black", error && "text-touki-red", legendClassName)}>
          {legend}
          {required ? <span aria-hidden="true" className="ms-0.5 text-touki-muted">*</span> : null}
        </legend>
        {hint ? <p id={hintId} className="text-label-md text-touki-muted">{hint}</p> : null}
        <div className={orientation === "horizontal" ? "flex flex-wrap gap-x-6" : "flex flex-col"}>
          {children}
        </div>
        {error ? (
          <p id={errorId} role="alert" className="mt-1 flex items-start gap-1.5 text-label-md text-touki-red">
            <WarningCircle weight="fill" size={16} aria-hidden="true" focusable="false" className="mt-px shrink-0" />
            <span>{error}</span>
          </p>
        ) : null}
      </fieldset>
    </RadioGroupPrimitive.Root>
  )
})
RadioGroup.displayName = "RadioGroup"

/** One choice: the radio and its words in a single 44px label row. */
export const Radio = React.forwardRef(({ value, children, hint, disabled, id, className, ...props }, ref) => {
  const auto = React.useId()
  const itemId = id ?? `${auto}radio`
  const textId = `${itemId}-label`
  const hintId = hint ? `${itemId}-hint` : undefined
  return (
    <label
      htmlFor={itemId}
      className={cn("flex min-h-11 cursor-pointer items-start gap-3 py-2.5 has-[:disabled]:cursor-not-allowed", className)}>
      <RadioGroupItem
        ref={ref}
        id={itemId}
        value={value}
        disabled={disabled}
        aria-labelledby={textId}
        aria-describedby={hintId}
        className="mt-0.5 group-data-[invalid]/radios:border-touki-red"
        {...props} />
      <span className="grid min-w-0 gap-0.5">
        <span id={textId} className={cn("text-body-md text-touki-black", disabled && "text-touki-muted")}>{children}</span>
        {hint ? <span id={hintId} className="text-label-md text-touki-muted">{hint}</span> : null}
      </span>
    </label>
  )
})
Radio.displayName = "Radio"
